import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { Link, useOutletContext } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type BatchDeliverPasswordResponse,
  type BatchPatchUserResponse,
  type BindFeishuBody,
  type CreateUserBody,
  type DeliverChannel,
  type DeliverPasswordResult,
  type PatchUserBody,
  type PublicRole,
  type PublicUser,
  type UserStatus,
  type UsersListResponse,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import {
  batchPatchUsersPath,
  clientDevicesPath,
  rolesPath,
  userByIdPath,
  usersPath,
} from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { Empty } from "../../components/Empty.js";
import { Modal } from "../../components/Modal.js";
import { PinyinMigrationDialog } from "./PinyinMigrationDialog.js";
import { BulkImportDialog } from "./feishu-import/BulkImportDialog.js";
import { StatusPill } from "../../components/StatusPill.js";
import { Table, type ColumnDef } from "../../components/Table.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextInput,
} from "../../components/FormField.js";
import type { OrgDetailContext } from "./context.js";
import { DosiaVersionCell, useDevicesByUser } from "./DosiaVersionCell.js";

type ModalKind =
  | { kind: "create" }
  | { kind: "edit"; user: PublicUser }
  | { kind: "reset"; user: PublicUser }
  | { kind: "bind"; user: PublicUser }
  | { kind: "confirm-disable"; user: PublicUser }
  | { kind: "confirm-delete"; user: PublicUser }
  | { kind: "deliver"; user: PublicUser }
  | { kind: "deliver-batch" }
  | null;

export function UsersPanel(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const { t } = useTranslation();
  const auth = useAtomValue(authAtom);
  const [status, setStatus] = useState<UserStatus | "">("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [q, setQ] = useState<string>("");
  // T1.3.12 · 默认排 disabled；用户勾「显示已删除」时加 ?includeDisabled=true
  const [includeDisabled, setIncludeDisabled] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [modal, setModal] = useState<ModalKind>(null);
  const [pinyinOpen, setPinyinOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [batchLoading, setBatchLoading] = useState(false);

  // 过滤条件变化时把页码重置回 1，避免空页/越界；同时清空选择
  const clearSelection = (): void => setSelectedUserIds(new Set());
  const updateStatus = (s: UserStatus | ""): void => {
    setStatus(s);
    setPage(1);
    clearSelection();
  };
  const updateRoleFilter = (r: string): void => {
    setRoleFilter(r);
    setPage(1);
    clearSelection();
  };
  const updateQ = (s: string): void => {
    setQ(s);
    setPage(1);
    clearSelection();
  };
  const updateIncludeDisabled = (v: boolean): void => {
    setIncludeDisabled(v);
    setPage(1);
    clearSelection();
  };

  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (roleFilter) qs.set("roleId", roleFilter);
  if (q.trim()) qs.set("q", q.trim());
  if (includeDisabled) qs.set("includeDisabled", "true");
  qs.set("page", String(page));
  const path = `${usersPath(org.code)}?${qs.toString()}`;
  const { data, loading, error, refetch } = useApiQuery<UsersListResponse>(
    path,
    [path],
  );
  const { data: roles } = useApiQuery<PublicRole[]>(rolesPath(org.code), [org.code]);
  // 「DOSIA 版本」列：按设备记的版本（拉取失败只让这一列显示「—」，不挡员工列表）。
  const devicesQuery = useApiQuery<unknown>(clientDevicesPath(org.code), [org.code]);
  const { byUser: devicesByUser, floor: minRecommendedVersion, lag: versionLag } = useDevicesByUser(
    devicesQuery.data,
    devicesQuery.loading,
    devicesQuery.error !== null,
  );

  const handleSubmitCreate = async (body: CreateUserBody): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall<PublicUser>("POST", usersPath(org.code), {
        body,
        token: auth.token,
      });
      setActionError(null);
      setModal(null);
      await refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "创建失败");
    }
  };

  const handlePatch = async (
    user: PublicUser,
    body: PatchUserBody,
  ): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall<PublicUser>("PATCH", userByIdPath(org.code, user.id), {
        body,
        token: auth.token,
      });
      setActionError(null);
      setModal(null);
      await refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "更新失败");
    }
  };

  const handleResetPassword = async (
    user: PublicUser,
    password: string,
  ): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("POST", userByIdPath(org.code, user.id, "/reset-password"), {
        body: { password },
        token: auth.token,
      });
      setActionError(null);
      setModal(null);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "重置失败");
    }
  };

  const handleBindFeishu = async (
    user: PublicUser,
    body: BindFeishuBody,
  ): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("POST", userByIdPath(org.code, user.id, "/bind-feishu"), {
        body,
        token: auth.token,
      });
      setActionError(null);
      setModal(null);
      await refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "绑定失败");
    }
  };

  const handleUnbindFeishu = useCallback(
    async (user: PublicUser): Promise<void> => {
      if (!auth) return;
      try {
        await apiCall("POST", userByIdPath(org.code, user.id, "/unbind-feishu"), {
          token: auth.token,
        });
        await refetch();
      } catch (err) {
        setActionError(err instanceof ApiError ? err.message : "解绑失败");
      }
    },
    [auth, org.code, refetch],
  );

  const handleDisable = async (user: PublicUser): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("DELETE", userByIdPath(org.code, user.id), {
        token: auth.token,
      });
      setActionError(null);
      setModal(null);
      await refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "禁用失败");
    }
  };

  const handlePurge = async (user: PublicUser): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("DELETE", userByIdPath(org.code, user.id, "/purge"), {
        token: auth.token,
      });
      setActionError(null);
      setModal(null);
      await refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "删除失败");
    }
  };

  const handleToggleSelect = useCallback((userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedUserIds((prev) => {
      const rows = data?.rows ?? [];
      const allVisibleSelected = rows.length > 0 && rows.every((r) => prev.has(r.id));
      const next = new Set(prev);
      for (const r of rows) {
        if (allVisibleSelected) next.delete(r.id);
        else next.add(r.id);
      }
      return next;
    });
  }, [data]);

  const handleBatchEnableDosia = async (): Promise<void> => {
    if (!auth || selectedUserIds.size === 0) return;
    setBatchLoading(true);
    setActionError(null);
    try {
      const res = await apiCall<BatchPatchUserResponse>(
        "POST",
        batchPatchUsersPath(org.code),
        {
          token: auth.token,
          body: {
            userIds: [...selectedUserIds],
            patch: { enableDosia: true },
          },
        },
      );
      setSelectedUserIds(new Set());
      await refetch();
      if (res.errors.length > 0) {
        setActionError(
          `批量启用完成：${res.modified} 成功 / ${res.skipped} 跳过 / ${res.errors.length} 失败`,
        );
      }
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "批量操作失败");
    } finally {
      setBatchLoading(false);
    }
  };

  const deliverSingle = useCallback(
    async (
      user: PublicUser,
      channels: DeliverChannel[],
    ): Promise<DeliverPasswordResult> => {
      if (!auth) throw new Error("未登录");
      return apiCall<DeliverPasswordResult>(
        "POST",
        userByIdPath(org.code, user.id, "/deliver-password"),
        { body: { channels }, token: auth.token },
      );
    },
    [auth, org.code],
  );

  const deliverBatch = useCallback(
    async (
      userIds: string[],
      channels: DeliverChannel[],
    ): Promise<BatchDeliverPasswordResponse> => {
      if (!auth) throw new Error("未登录");
      return apiCall<BatchDeliverPasswordResponse>(
        "POST",
        usersPath(org.code, "/deliver-password"),
        { body: { userIds, channels }, token: auth.token },
      );
    },
    [auth, org.code],
  );

  const columns: ColumnDef<PublicUser>[] = [
    {
      // 身份统一展示「姓名 + 手机号」：主行 = 飞书姓名（未进组织架构的手动用户则显示
      // 创建用户名 user）；副行 = 登录手机号。手机号缺失时灰色提示（非故障：仍可用
      // 用户名+密码登录，故不用 amber/红）。原始 user handle 只在编辑态可见，不在列表露出。
      header: "用户",
      render: (u) => (
        <div className="flex flex-col">
          <span className="text-dosia-text">{u.feishu?.name ?? u.user}</span>
          {u.mobile ? (
            <span className="font-mono text-xs text-dosia-text-dim">{u.mobile}</span>
          ) : (
            <span className="text-xs text-dosia-text-dim">未设置手机号</span>
          )}
        </div>
      ),
    },
    {
      header: "角色",
      render: (u) => u.role ?? <span className="text-dosia-text-dim">未分配</span>,
    },
    {
      header: "状态",
      width: "w-32",
      render: (u) => (
        <div className="flex flex-col gap-0.5">
          <StatusPill status={u.status} />
          {u.status === "pending" ? (
            <span className="text-[10px] text-amber-700">等待 AI 推荐</span>
          ) : null}
        </div>
      ),
    },
    {
      header: "飞书",
      render: (u) =>
        u.feishu?.openId ? (
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-xs">{u.feishu.openId}</span>
            {u.feishu.unionId ? (
              <span className="font-mono text-[10px] text-dosia-text-dim">
                union {u.feishu.unionId}
              </span>
            ) : (
              <span className="text-[10px] text-amber-700">缺 union_id · 需跑飞书同步</span>
            )}
          </div>
        ) : (
          <span className="text-dosia-text-dim text-xs">未绑定</span>
        ),
    },
    {
      header: t("admin.users.dosiaVersion.header"),
      width: "w-36",
      render: (u) => (
        <DosiaVersionCell userId={u.user} byUser={devicesByUser} floor={minRecommendedVersion} lag={versionLag} />
      ),
    },
    {
      header: "操作",
      width: "w-80",
      render: (u) => (
        <div className="flex gap-1 flex-wrap">
          {u.status === "pending" ? (
            <Link
              to="../onboarding?step=3"
              className="text-xs text-dosia-accent hover:underline"
            >
              查看推荐任务
            </Link>
          ) : null}
          <button
            type="button"
            className="text-xs text-dosia-accent hover:underline"
            onClick={() => setModal({ kind: "edit", user: u })}
          >
            编辑
          </button>
          <button
            type="button"
            className="text-xs text-dosia-accent hover:underline"
            onClick={() => setModal({ kind: "reset", user: u })}
          >
            重置密码
          </button>
          <button
            type="button"
            className="text-xs text-dosia-accent hover:underline"
            onClick={() => setModal({ kind: "deliver", user: u })}
          >
            下发密码
          </button>
          {u.feishu?.openId ? (
            <button
              type="button"
              className="text-xs text-dosia-text-secondary hover:underline"
              onClick={() => void handleUnbindFeishu(u)}
            >
              解绑飞书
            </button>
          ) : (
            <button
              type="button"
              className="text-xs text-dosia-accent hover:underline"
              onClick={() => setModal({ kind: "bind", user: u })}
            >
              绑定飞书
            </button>
          )}
          {u.status !== "disabled" ? (
            <button
              type="button"
              className="text-xs text-amber-700 hover:underline"
              onClick={() => setModal({ kind: "confirm-disable", user: u })}
            >
              禁用
            </button>
          ) : null}
          <button
            type="button"
            className="text-xs text-red-400 hover:underline"
            onClick={() => setModal({ kind: "confirm-delete", user: u })}
          >
            删除
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select
          value={status}
          onChange={(e) => updateStatus(e.target.value as UserStatus | "")}
          className="w-32"
          aria-label="按状态筛选"
        >
          <option value="">全部状态</option>
          <option value="active">正常</option>
          <option value="disabled">已禁用</option>
          <option value="pending">待处理</option>
        </Select>
        <Select
          value={roleFilter}
          onChange={(e) => updateRoleFilter(e.target.value)}
          className="w-40"
          aria-label="按角色筛选"
        >
          <option value="">全部角色</option>
          {(roles ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.code}
            </option>
          ))}
        </Select>
        <TextInput
          value={q}
          onChange={(e) => updateQ(e.target.value)}
          placeholder="搜索 user 名 / 手机号 / 飞书姓名"
          className="w-64"
        />
        <label className="flex items-center gap-1 text-xs text-dosia-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => updateIncludeDisabled(e.target.checked)}
            aria-label="显示已删除"
          />
          显示已删除
        </label>
        <div className="flex-1" />
        <SecondaryButton onClick={() => setPinyinOpen(true)}>
          拼音匹配飞书
        </SecondaryButton>
        <SecondaryButton onClick={() => setBulkImportOpen(true)}>
          + 从飞书部门导入
        </SecondaryButton>
        <PrimaryButton onClick={() => setModal({ kind: "create" })}>
          + 创建用户
        </PrimaryButton>
      </div>

      {error ? <ErrorBanner>{error.message}</ErrorBanner> : null}
      {actionError ? (
        <ErrorBanner onDismiss={() => setActionError(null)}>
          {actionError}
        </ErrorBanner>
      ) : null}

      {selectedUserIds.size > 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
          <span className="text-amber-500">
            已选择 <strong>{selectedUserIds.size}</strong> 个用户
          </span>
          <div className="flex-1" />
          <PrimaryButton
            onClick={() => void handleBatchEnableDosia()}
            disabled={batchLoading}
          >
            {batchLoading ? "处理中..." : "批量启用 DOSIA"}
          </PrimaryButton>
          <SecondaryButton onClick={() => setModal({ kind: "deliver-batch" })}>
            批量下发密码
          </SecondaryButton>
          <SecondaryButton onClick={clearSelection}>取消选择</SecondaryButton>
        </div>
      ) : null}

      <Table<PublicUser>
        columns={columns}
        rows={data?.rows ?? []}
        rowKey={(u) => u.id}
        loading={loading}
        empty={<Empty title="暂无用户" description="点右上角创建第一个" />}
        selectable
        selectedIds={selectedUserIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
      />
      {data
        ? (() => {
            const totalPages = Math.max(
              1,
              Math.ceil(data.total / data.pageSize),
            );
            return (
              <div className="flex items-center justify-between text-xs text-dosia-text-secondary">
                <span>
                  共 {data.total} 条 · 第 {data.page} / {totalPages} 页 · 每页{" "}
                  {data.pageSize}
                </span>
                <div className="flex gap-2">
                  <SecondaryButton
                    disabled={page <= 1}
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      clearSelection();
                    }}
                  >
                    上一页
                  </SecondaryButton>
                  <SecondaryButton
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage((p) => p + 1);
                      clearSelection();
                    }}
                  >
                    下一页
                  </SecondaryButton>
                </div>
              </div>
            );
          })()
        : null}

      {modal?.kind === "create" ? (
        <CreateUserDialog
          roles={roles ?? []}
          onCancel={() => setModal(null)}
          onSubmit={handleSubmitCreate}
        />
      ) : null}
      {modal?.kind === "edit" ? (
        <EditUserDialog
          user={modal.user}
          roles={roles ?? []}
          onCancel={() => setModal(null)}
          onSubmit={(body) => handlePatch(modal.user, body)}
        />
      ) : null}
      {modal?.kind === "reset" ? (
        <ResetPasswordDialog
          user={modal.user}
          onCancel={() => setModal(null)}
          onSubmit={(pw) => handleResetPassword(modal.user, pw)}
        />
      ) : null}
      {modal?.kind === "bind" ? (
        <BindFeishuDialog
          user={modal.user}
          onCancel={() => setModal(null)}
          onSubmit={(body) => handleBindFeishu(modal.user, body)}
        />
      ) : null}
      {modal?.kind === "deliver" ? (
        <DeliverPasswordDialog
          users={[modal.user]}
          onCancel={() => setModal(null)}
          onDeliver={async (channels) => {
            const result = await deliverSingle(modal.user, channels);
            return [result];
          }}
        />
      ) : null}
      {modal?.kind === "deliver-batch" ? (
        <DeliverPasswordDialog
          users={(data?.rows ?? []).filter((r) => selectedUserIds.has(r.id))}
          onCancel={() => setModal(null)}
          onDeliver={async (channels) => {
            const res = await deliverBatch([...selectedUserIds], channels);
            if (res.errors.length > 0) {
              setActionError(
                `批量下发：${res.results.length} 成功 / ${res.errors.length} 失败`,
              );
            }
            return res.results;
          }}
        />
      ) : null}
      <PinyinMigrationDialog
        orgCode={org.code}
        open={pinyinOpen}
        onClose={(applied) => {
          setPinyinOpen(false);
          if (applied) void refetch();
        }}
      />

      {bulkImportOpen ? (
        <BulkImportDialog
          orgCode={org.code}
          roles={roles ?? []}
          onClose={() => setBulkImportOpen(false)}
          onCompleted={() => void refetch()}
        />
      ) : null}

      {modal?.kind === "confirm-disable" ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title="确认禁用用户？"
          footer={
            <>
              <SecondaryButton onClick={() => setModal(null)}>取消</SecondaryButton>
              <PrimaryButton onClick={() => void handleDisable(modal.user)}>
                确认禁用
              </PrimaryButton>
            </>
          }
        >
          <p className="text-sm text-dosia-text">
            确定要禁用用户 <span className="font-mono">{modal.user.user}</span> 吗？
            该用户登录将被拒绝；操作可通过 PATCH status=active 撤销。
          </p>
        </Modal>
      ) : null}

      {modal?.kind === "confirm-delete" ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title="确认删除用户？"
          footer={
            <>
              <SecondaryButton onClick={() => setModal(null)}>取消</SecondaryButton>
              <button
                type="button"
                className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/20"
                onClick={() => void handlePurge(modal.user)}
              >
                确认删除
              </button>
            </>
          }
        >
          <p className="text-sm text-dosia-text">
            将<strong>物理删除</strong>用户{" "}
            <span className="font-mono">
              {modal.user.feishu?.name ?? modal.user.user}
            </span>
            （{modal.user.user}），此操作<strong>不可恢复</strong>。若只是暂停使用请改用「禁用」。
          </p>
          <div className="mt-2 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
            删除后该用户所有登录凭证立即失效，飞书 openId 绑定一并移除。
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function CreateUserDialog({
  roles,
  onCancel,
  onSubmit,
}: {
  roles: PublicRole[];
  onCancel: () => void;
  onSubmit: (body: CreateUserBody) => Promise<void>;
}): JSX.Element {
  const [user, setUser] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState<string>("");
  const [status, setStatus] = useState<UserStatus>("active");
  const [enableDosia, setEnableDosia] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const trimmedMobile = mobile.trim();
  const mobileValid = trimmedMobile === "" || /^\d{11}$/.test(trimmedMobile);
  const ready =
    user.trim().length > 0 && password.length > 0 && mobileValid;

  return (
    <Modal
      open
      onClose={onCancel}
      title="创建用户"
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={!ready || submitting}
            onClick={async () => {
              setSubmitting(true);
              await onSubmit({
                user: user.trim(),
                mobile: trimmedMobile || undefined,
                password,
                roleId: roleId || undefined,
                status,
                enableDosia,
              });
              setSubmitting(false);
            }}
          >
            创建
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label="用户名" required hint="内部显示名 / 稳定 handle，非登录键">
          <TextInput
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="alice"
            aria-label="用户名"
          />
        </FormField>
        <FormField
          label="登录手机号"
          hint={
            mobileValid
              ? "默认用手机号登录（11 位）。留空则用上面的用户名登录，可后续补录手机号。"
              : undefined
          }
        >
          <TextInput
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="13800138000"
            aria-label="登录手机号"
          />
          {!mobileValid ? (
            <span className="mt-1 block text-xs text-amber-700">
              手机号需为 11 位数字
            </span>
          ) : null}
        </FormField>
        <FormField label="初始密码" required hint="员工首次登录后可自行修改">
          <TextInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-label="初始密码"
          />
        </FormField>
        <FormField label="角色">
          <Select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">不分配</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} ({r.name})
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="初始状态">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as UserStatus)}
          >
            <option value="active">正常</option>
            <option value="pending">待处理</option>
            <option value="disabled">禁用</option>
          </Select>
        </FormField>
        {/* T1.3.8 · 启用 dosia desktop 开关 + 内嵌确认提示（amber，非 bug 不红色） */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enableDosia}
            onChange={(e) => setEnableDosia(e.target.checked)}
            className="mt-1"
            aria-label="启用 dosia desktop"
          />
          <div className="flex flex-col gap-1">
            <span className="text-sm text-dosia-text">启用 dosia desktop</span>
            {enableDosia ? (
              <span className="text-xs text-amber-500 bg-amber-500/15 border border-amber-500/30 rounded px-2 py-1">
                将在 ByteSpike 创建影子账号；server 自动生成
                dosiaEmployeeId，一旦生成不可更改。
              </span>
            ) : (
              <span className="text-xs text-dosia-text-dim">
                启用后 server 自动生成 dosiaEmployeeId，员工方可登录 dosia desktop。
              </span>
            )}
          </div>
        </label>
      </div>
    </Modal>
  );
}

function EditUserDialog({
  user,
  roles,
  onCancel,
  onSubmit,
}: {
  user: PublicUser;
  roles: PublicRole[];
  onCancel: () => void;
  onSubmit: (body: PatchUserBody) => Promise<void>;
}): JSX.Element {
  const [userName, setUserName] = useState<string>(user.user);
  const [status, setStatus] = useState<UserStatus>(user.status);
  const [roleId, setRoleId] = useState<string>(user.roleId ?? "");
  const [enableDosia, setEnableDosia] = useState<boolean>(user.enableDosia);
  const [submitting, setSubmitting] = useState(false);

  // T1.3.9 · dosiaEmployeeId 一旦非空 readOnly 灰掉，不可改也不可清
  const dosiaEmployeeIdLocked = Boolean(user.dosiaEmployeeId);
  const togglingDosiaOn = enableDosia && !user.enableDosia;

  return (
    <Modal
      open
      onClose={onCancel}
      title={`编辑用户 ${user.user}`}
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={submitting || userName.trim().length === 0}
            onClick={async () => {
              setSubmitting(true);
              const body: PatchUserBody = {
                status,
                roleId: roleId === "" ? null : roleId,
              };
              const trimmedUser = userName.trim();
              if (trimmedUser !== user.user) body.user = trimmedUser;
              if (enableDosia !== user.enableDosia) body.enableDosia = enableDosia;
              await onSubmit(body);
              setSubmitting(false);
            }}
          >
            保存
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField
          label="用户名"
          required
          hint={
            user.mobile
              ? `内部显示名 / 稳定 handle（登录用手机号 ${user.mobile}）`
              : "内部显示名 / 稳定 handle（登录键为手机号，当前未设置）"
          }
        >
          <TextInput
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            aria-label="用户名"
          />
        </FormField>
        <FormField label="状态">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as UserStatus)}
          >
            <option value="active">正常</option>
            <option value="pending">待处理</option>
            <option value="disabled">禁用</option>
          </Select>
        </FormField>
        <FormField label="角色" hint="选 -- 不分配 -- 等同 $unset roleId">
          <Select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">-- 不分配 --</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} ({r.name})
              </option>
            ))}
          </Select>
        </FormField>

        {/* T1.3.8 · enableDosia 开关；togglingDosiaOn 时显示 amber 警示 */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enableDosia}
            onChange={(e) => setEnableDosia(e.target.checked)}
            className="mt-1"
            aria-label="启用 dosia desktop"
          />
          <div className="flex flex-col gap-1">
            <span className="text-sm text-dosia-text">启用 dosia desktop</span>
            {togglingDosiaOn ? (
              <span className="text-xs text-amber-500 bg-amber-500/15 border border-amber-500/30 rounded px-2 py-1">
                确认启用？将在 ByteSpike 创建影子账号，server 自动生成
                dosiaEmployeeId 后不可更改。
              </span>
            ) : null}
          </div>
        </label>

        {/* T1.3.9 · dosiaEmployeeId 已设置 → readOnly 灰掉 */}
        {user.dosiaEmployeeId || enableDosia ? (
          <FormField
            label="dosiaEmployeeId"
            hint={
              dosiaEmployeeIdLocked
                ? "已生成，immutable（schema-level + handler 双保险）"
                : "server 创建/启用 dosia 时自动生成"
            }
          >
            <TextInput
              value={user.dosiaEmployeeId ?? "（启用 dosia 并保存后由 server 生成）"}
              disabled
              readOnly
              aria-label="dosiaEmployeeId"
            />
          </FormField>
        ) : null}
      </div>
    </Modal>
  );
}

function ResetPasswordDialog({
  user,
  onCancel,
  onSubmit,
}: {
  user: PublicUser;
  onCancel: () => void;
  onSubmit: (password: string) => Promise<void>;
}): JSX.Element {
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal
      open
      onClose={onCancel}
      title={`重置密码 - ${user.user}`}
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={pw.length === 0 || submitting}
            onClick={async () => {
              setSubmitting(true);
              await onSubmit(pw);
              setSubmitting(false);
            }}
          >
            重置
          </PrimaryButton>
        </>
      }
    >
      <FormField
        label="新密码"
        required
        hint="密码不返回到前端；后端 bcrypt cost-12 加密"
      >
        <TextInput
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          aria-label="新密码"
        />
      </FormField>
    </Modal>
  );
}

function BindFeishuDialog({
  user,
  onCancel,
  onSubmit,
}: {
  user: PublicUser;
  onCancel: () => void;
  onSubmit: (body: BindFeishuBody) => Promise<void>;
}): JSX.Element {
  const [openId, setOpenId] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal
      open
      onClose={onCancel}
      title={`绑定飞书 - ${user.user}`}
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={!openId.trim() || submitting}
            onClick={async () => {
              setSubmitting(true);
              await onSubmit({
                openId: openId.trim(),
                name: name || undefined,
                mobile: mobile || undefined,
                email: email || undefined,
              });
              setSubmitting(false);
            }}
          >
            绑定
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label="飞书 openId" required>
          <TextInput
            value={openId}
            onChange={(e) => setOpenId(e.target.value)}
            placeholder="ou_xxx"
            aria-label="飞书 openId"
          />
        </FormField>
        <FormField label="姓名">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="手机">
          <TextInput value={mobile} onChange={(e) => setMobile(e.target.value)} />
        </FormField>
        <FormField label="邮箱">
          <TextInput value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormField>
      </div>
    </Modal>
  );
}

/**
 * 密码下发弹窗（单/批量共用）：选渠道（飞书私信 / 仅生成并复制）→ 调下发端点 →
 * 展示结果。copy 渠道显示明文临时密码 + 复制按钮；feishu 渠道显示送达状态。
 * 批量额外提供「导出 CSV」（列：手机号, 用户名, 初始密码）。
 */
function DeliverPasswordDialog({
  users,
  onCancel,
  onDeliver,
}: {
  users: PublicUser[];
  onCancel: () => void;
  onDeliver: (channels: DeliverChannel[]) => Promise<DeliverPasswordResult[]>;
}): JSX.Element {
  const [feishu, setFeishu] = useState(false);
  const [copy, setCopy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<DeliverPasswordResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const channels: DeliverChannel[] = [
    ...(feishu ? (["feishu"] as const) : []),
    ...(copy ? (["copy"] as const) : []),
  ];
  const isBatch = users.length !== 1;

  const exportCsv = (): void => {
    if (!results) return;
    const header = "手机号,用户名,初始密码";
    const rows = results.map(
      (r) => `${r.mobile ?? ""},${r.user},${r.generatedPassword ?? ""}`,
    );
    const csv = `﻿${[header, ...rows].join("\n")}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `password-delivery-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title={isBatch ? `批量下发密码（${users.length} 人）` : `下发密码 - ${users[0]?.user ?? ""}`}
      footer={
        results ? (
          <>
            {isBatch ? (
              <SecondaryButton onClick={exportCsv}>导出 CSV</SecondaryButton>
            ) : null}
            <PrimaryButton onClick={onCancel}>完成</PrimaryButton>
          </>
        ) : (
          <>
            <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
            <PrimaryButton
              disabled={channels.length === 0 || submitting}
              onClick={async () => {
                setSubmitting(true);
                setError(null);
                try {
                  const res = await onDeliver(channels);
                  setResults(res);
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : "下发失败");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {submitting ? "下发中..." : "下发"}
            </PrimaryButton>
          </>
        )
      }
    >
      {results ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-dosia-text">下发完成，共 {results.length} 条：</p>
          <div className="max-h-72 overflow-auto rounded border border-dosia-border">
            <table className="w-full text-xs">
              <thead className="bg-dosia-bg-secondary text-dosia-text-secondary">
                <tr>
                  <th className="px-2 py-1 text-left">用户名</th>
                  <th className="px-2 py-1 text-left">手机号</th>
                  <th className="px-2 py-1 text-left">初始密码</th>
                  <th className="px-2 py-1 text-left">飞书</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.userId} className="border-t border-dosia-border">
                    <td className="px-2 py-1 font-mono">{r.user}</td>
                    <td className="px-2 py-1 font-mono">{r.mobile ?? "-"}</td>
                    <td className="px-2 py-1">
                      {r.generatedPassword ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-mono">{r.generatedPassword}</span>
                          <button
                            type="button"
                            className="text-dosia-accent hover:underline"
                            onClick={() =>
                              void navigator.clipboard?.writeText(
                                r.generatedPassword ?? "",
                              )
                            }
                          >
                            复制
                          </button>
                        </span>
                      ) : (
                        <span className="text-dosia-text-dim">-</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {r.feishu == null ? (
                        <span className="text-dosia-text-dim">-</span>
                      ) : r.feishu.ok ? (
                        <span className="text-emerald-600">已送达</span>
                      ) : (
                        <span className="text-amber-700" title={r.feishu.reason}>
                          未送达
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-dosia-text-dim">
            明文初始密码仅本次可见，关闭后无法再取回；请及时复制或导出 CSV 转告员工。
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-dosia-text">
            将为{isBatch ? ` ${users.length} 名员工` : "该员工"}重新生成初始密码，并通过所选渠道送达。
          </p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={feishu}
              onChange={(e) => setFeishu(e.target.checked)}
              className="mt-1"
              aria-label="飞书私信"
            />
            <div className="flex flex-col">
              <span className="text-sm text-dosia-text">飞书私信</span>
              <span className="text-xs text-dosia-text-dim">
                向员工飞书发送「登录手机号 + 初始密码」。未绑定飞书 / 组织未连飞书的员工该渠道会降级为未送达。
              </span>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={copy}
              onChange={(e) => setCopy(e.target.checked)}
              className="mt-1"
              aria-label="仅生成并复制"
            />
            <div className="flex flex-col">
              <span className="text-sm text-dosia-text">仅生成并复制</span>
              <span className="text-xs text-dosia-text-dim">
                在结果里显示明文初始密码，由管理员自行复制或导出 CSV 转告。
              </span>
            </div>
          </label>
          {error ? (
            <div className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
              {error}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
